import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { adminListLinkSuggestions } from '@/lib/api';
import {
  CalendarRange,
  CreditCard,
  Globe2,
  Home as HomeIcon,
  IdCard,
  Link2,
  Menu,
  Settings,
  Shield,
  Trophy,
  Users,
  Server,
  Video,
  Wallet,
  Wrench,
  Sparkles,
  X,
} from 'lucide-react';

const NAV = [
  { to: '/admin', label: 'Inicio', icon: HomeIcon, end: true, phase: '01' },
  { to: '/admin/usuarios', label: 'Usuarios', icon: Users, phase: '01' },
  { to: '/admin/fichas-jugadores', label: 'Fichas de jugadores', icon: IdCard, phase: '08' },
  { to: '/admin/sugerencias-vinculacion', label: 'Vinculaciones pendientes', icon: Link2, phase: '08', badgeKey: 'pendingSuggestions' },
  { to: '/admin/player-intelligence/importar', label: 'Player Intelligence · Importar', icon: Sparkles, phase: '3B' },
  { to: '/admin/deportes', label: 'Deportes', icon: Trophy, phase: '02' },
  { to: '/admin/territorios', label: 'Territorios', icon: Globe2, phase: '02' },
  { to: '/admin/clubes', label: 'Clubes', icon: Shield, phase: '02' },
  { to: '/admin/equipos', label: 'Equipos', icon: Users, phase: '02' },
  { to: '/admin/eventos', label: 'Partidos', icon: CalendarRange, phase: '03' },
  { to: '/admin/auditoria', label: 'Auditoría', icon: Shield, phase: '04' },
  { to: '/admin/planes', label: 'Planes', icon: Wallet, phase: '05A' },
  { to: '/admin/compras', label: 'Compras', icon: CreditCard, phase: '05A' },
  { to: '/admin/pagos', label: 'Pagos', icon: CreditCard, phase: '05A' },
  { to: '/admin/suscripciones', label: 'Suscripciones', icon: Wallet, phase: '05A' },
  { to: '/admin/accesos', label: 'Accesos', icon: Shield, phase: '05A' },
  { to: '/admin/revenue-share', label: 'Revenue share', icon: Wrench, phase: '05A' },
  { to: '/admin/descargas', label: 'Descargas', icon: Video, phase: '05B' },
  { to: '/admin/video-providers', label: 'Providers', icon: Server, phase: '06A' },
  { to: '/admin/configuracion', label: 'Configuración', icon: Settings, phase: '04' },
];

export const AdminLayout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [pendingSuggestions, setPendingSuggestions] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await adminListLinkSuggestions({ status: 'pending' });
        if (alive) setPendingSuggestions(res?.pending_count || 0);
      } catch (err) {
        // Silent: no-op si el usuario no tiene permisos (404/401 controlados aguas arriba)
      }
    })();
    return () => { alive = false; };
  }, []);

  const badgeValues = { pendingSuggestions };

  const SidebarContent = () => (
    <nav className="flex flex-col gap-1 p-3">
      <div className="px-3 pt-2 pb-3">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ns-text-subtle)] font-bold">
          Administración
        </p>
        <p className="text-xs text-[var(--ns-text-muted)] mt-1">Panel interno NetScouters</p>
      </div>
      {NAV.map(({ to, label, icon: Icon, end, phase, badgeKey }) => {
        const isReady = ['01', '02', '03', '04', '05A', '05B', '08'].includes(phase);
        const badgeCount = badgeKey ? badgeValues[badgeKey] : 0;
        return (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${to.replace(/\//g, '-')}`}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive
                  ? 'bg-gradient-to-r from-[var(--ns-amber-100)] to-[var(--ns-amber-50)] text-[var(--ns-gold-700)] font-semibold shadow-[0_2px_8px_rgba(245,158,11,0.18)]'
                  : 'text-[var(--ns-text)] hover:bg-[var(--ns-bg-2)]'
              }`
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate">{label}</span>
            {badgeCount > 0 && (
              <span
                data-testid={`admin-nav-badge-${to.replace(/\//g, '-')}`}
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] shadow-sm min-w-[20px] text-center"
              >
                {badgeCount}
              </span>
            )}
            {!isReady && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--ns-bg-2)] text-[var(--ns-text-subtle)] border border-[var(--ns-border)]">
                F{phase}
              </span>
            )}
          </NavLink>
        );
      })}
      <div className="mt-4 mx-3 p-3 rounded-xl bg-[var(--ns-amber-50)] border border-[var(--ns-gold)]/30">
        <div className="flex items-start gap-2">
          <Wrench className="h-4 w-4 text-[var(--ns-gold-700)] mt-0.5" />
          <div>
            <p className="text-[11px] font-semibold text-[var(--ns-gold-700)] uppercase tracking-wider">
              Admin shell
            </p>
            <p className="text-[11px] text-[var(--ns-text-muted)] mt-0.5 leading-snug">
              Versión temprana. Las secciones marcadas con "F0X" se completarán en esa fase.
            </p>
          </div>
        </div>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[var(--ns-bg)] text-[var(--ns-text)]">
      <Header />
      <div className="flex-1 flex w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 gap-6">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-20 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)] overflow-hidden">
            <SidebarContent />
          </div>
        </aside>

        {/* Sidebar mobile (drawer) */}
        {open && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <div className="absolute top-0 left-0 bottom-0 w-72 bg-white shadow-2xl overflow-y-auto">
              <div className="flex items-center justify-between p-3 border-b border-[var(--ns-border)]">
                <Link to="/admin" onClick={() => setOpen(false)} className="font-display uppercase tracking-wide text-[var(--ns-text)]">
                  Admin
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setOpen(false)}
                  aria-label="Cerrar menú"
                  data-testid="admin-sidebar-close"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <SidebarContent />
            </div>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 min-w-0">
          {/* Toggle mobile */}
          <div className="lg:hidden mb-4">
            <Button
              variant="outline"
              onClick={() => setOpen(true)}
              data-testid="admin-sidebar-toggle"
              className="rounded-lg bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)]"
            >
              <Menu className="h-4 w-4 mr-2" />
              Menú admin
            </Button>
          </div>
          {children}
        </main>
      </div>
      <Footer />
    </div>
  );
};
